import { MouseEvent, ChangeEvent, useContext, useEffect, useState } from 'react';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import { fetchSignInMethodsForEmail, User } from 'firebase/auth';
import { MetadataError } from '@ep/error-handling';
import { LoginState } from './LoginModal';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { validateEmail } from '../../../lib/validation/text_validation';
import { auth } from '../../../lib/google/firebase';
import type { MicrosoftProviderOptions } from '../../../lib/google/firebase';
import { getTenantDomainAuthMethodFromEmail } from '../../../lib/auth/auth.util';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { ReactComponent as MicrosoftLogo } from '../../../assets/icons/microsoft_logo.svg';
import { BodyText, Subheading } from '../../shared/typography/PrimaryTypography';
import { TenantAuthMethod } from '@esg/esg-global-types';

const FEDERATED_SIGN_IN_METHODS = ['microsoft.com'];

interface LoginIdentifierProps {
  email: string;
  setEmail: (email: string) => void;
  setSignInUser: (user: User | null) => void;
  setShowRecaptchaContainer: (state: boolean) => void;
  handleLoginState: (login_state: LoginState) => void;
  resetVerifyTimeout: (seconds: number) => void;
  handleMicrosoftSignIn: (event: MouseEvent, options?: MicrosoftProviderOptions) => Promise<void>;
}

/**
 * Handles the initial Email & Password sign in of the login flow.
 * @param {(user: User | null): void} setSignInUser Sets the global user state to be used in application.
 * @param {(seconds: number): void} resetVerifyTimeout Resets the counter for preventing instant clicking of verification steps.
 * @param {(login_state: LoginState): void} handleLoginState Handles the changing of modal state to various parts of the login flow.
 * @returns {JSX.Element}
 */
const LoginIdentifier = ({
  email,
  setEmail,
  setShowRecaptchaContainer,
  handleLoginState,
  handleMicrosoftSignIn
}: LoginIdentifierProps): JSX.Element => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [activateSignIn, setActivateSignIn] = useState<boolean>(false);
  const [emailFocused, setEmailFocus] = useState<boolean>(false);
  const [validEmail, setValidEmail] = useState<boolean>(false);
  const [domainCheckLoad, setDomainCheckLoad] = useState<boolean>(false);

  // Handles changing of email input state.
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setShowRecaptchaContainer(false);
    setEmail(event.target.value);
  };

  const handleEmailContinue = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDomainCheckLoad(true);
    try {
      const auth_method: TenantAuthMethod | null = await getTenantDomainAuthMethodFromEmail(email);

      // Go to password MFA flow for non-sso users.
      if (!auth_method || auth_method === 'email_password') {
        // Check if this is a migrated SSO user who has not yet set a password.
        const sign_in_methods: Array<string> = await fetchSignInMethodsForEmail(
          auth,
          email.toLowerCase()
        );
        if (
          sign_in_methods.some((method: string) => FEDERATED_SIGN_IN_METHODS.includes(method)) &&
          !sign_in_methods.includes('password')
        ) {
          handleLoginState('login_link_password');
          return;
        }
        handleLoginState('login_password');
        return;
      }

      await handleMicrosoftSignIn(event);
    } catch (err: unknown) {
      const tracking_id = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: LoginIdentifier failed on an unknown error while calling handleEmailContinue.',
          { email },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Failed to continue. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setDomainCheckLoad(false);
    }
  };

  // Handles debounce for email input and validates if email contains required characters.
  useEffect(() => {
    setTimeout(() => {
      setValidEmail(validateEmail(email));
    }, 500);
    return () => {
      null;
    };
  }, [email, emailFocused]);

  // Handles checking if email is valid to activate sign in button.
  useEffect(() => {
    setActivateSignIn(validEmail);
  }, [validEmail]);

  return (
    <Box>
      <Subheading weight="Medium" color="primary" sx={{ textAlign: 'center', mb: 4 }}>
        Welcome
      </Subheading>
      <BodyText sx={{ textAlign: 'center', my: 2 }}>Log in to Syntiro</BodyText>
      <form>
        <TextField
          error={!validEmail && emailFocused}
          onBlur={() => {
            setEmailFocus(true);
          }}
          data-testid="email-input"
          id="email"
          label="Email Address"
          type="email"
          fullWidth
          variant="outlined"
          onInput={handleEmailChange}
          value={email}
        />
        <PrimaryButton
          data-testid="sign-in-button"
          disabled={!activateSignIn}
          loading={domainCheckLoad}
          sx={{ mt: 4, width: '100%' }}
          onClick={handleEmailContinue}
          id="sign-in-button"
          type="submit"
        >
          CONTINUE
        </PrimaryButton>
      </form>
      <Box sx={{ my: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Divider sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          OR
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleMicrosoftSignIn}
        startIcon={<MicrosoftLogo />}
        sx={{
          textTransform: 'none',
          color: '#000000',
          borderColor: '#8c8c8c',
          backgroundColor: '#ffffff',
          fontSize: '15px',
          fontWeight: 400,
          justifyContent: 'flex-start',
          paddingX: 3,
          paddingY: 1.5,
          '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#8c8c8c'
          }
        }}
      >
        Continue with Microsoft
      </Button>
      <Button
        data-testid="microsoft-account-picker-button"
        fullWidth
        variant="text"
        onClick={(event: MouseEvent<HTMLButtonElement>) =>
          handleMicrosoftSignIn(event, { force_account_picker: true })
        }
        sx={{
          textTransform: 'none',
          color: '#053d5d',
          fontSize: '0.85rem',
          fontWeight: 400,
          mt: 1,
          textDecoration: 'underline',
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline'
          }
        }}
      >
        Sign in with a different Microsoft account
      </Button>
      <Typography sx={{ textAlign: 'center', color: '#053d5d', mt: 3, fontSize: '0.9rem' }}>
        Don&apos;t have an account yet? <a href="/contact">Contact Us</a>
      </Typography>
    </Box>
  );
};

export default LoginIdentifier;
